import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  LayoutDashboard,
  BarChart3, 
  Settings, 
  Crown,
  Globe,
  LogOut,
  Menu,
  X,
  ChevronRight,
  Shield,
  Receipt,
  Zap,
  Link2,
  Target,
  Radio,
  Send,
  Sun,
  Moon,
  Monitor,
  Tag,
  Users,
  Eye,
  ChevronsUpDown,
  Check
} from 'lucide-react';
import { useAuthStore } from '../../store/authStore';
import { useThemeStore, type Theme } from '../../store/themeStore';
import { useAccountStore, useEffectivePlan } from '../../store/accountStore';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { LanguageSwitcher } from '../Common/LanguageSwitcher';
import { NotificationBell } from '../Common/NotificationBell';
import { hasFeature, type FeatureKey } from '../../constants/planLimits';
import { openUpgradeGate } from '../../store/upgradeGateStore';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001/api';

interface UserLayoutProps {
  children: React.ReactNode;
}

export const UserLayout: React.FC<UserLayoutProps> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation('common');
  const { user, logout, impersonating, stopImpersonating } = useAuthStore();
  const { theme, resolved, setTheme } = useThemeStore();
  const accounts = useAccountStore((s) => s.accounts);
  const activeAccountId = useAccountStore((s) => s.activeAccountId);
  const loadAccounts = useAccountStore((s) => s.loadAccounts);
  const setActiveAccount = useAccountStore((s) => s.setActiveAccount);
  const effectivePlan = useEffectivePlan();
  const [accountMenuOpen, setAccountMenuOpen] = useState(false);

  // Resolve the workspace the user is currently acting in. Falls back to
  // their own account while the list is still loading.
  const selfId = user?.id || null;
  const effectiveAccountId = activeAccountId || selfId;
  const activeAccount = accounts.find((a) => a.id === effectiveAccountId) || null;
  const activeRole = activeAccount?.role || 'owner';
  const isViewer = activeRole === 'viewer';
  const inTeamWorkspace = !!activeAccount && !activeAccount.isSelf;

  // Pull the list of workspaces the user can act in once on mount. The
  // store re-validates the persisted active workspace against the server
  // response (handles revoked memberships gracefully).
  useEffect(() => {
    void loadAccounts();
  }, [loadAccounts]);

  const handleSwitchAccount = (id: string) => {
    setAccountMenuOpen(false);
    if (id === effectiveAccountId) return;
    setActiveAccount(id);
    // Hard reload onto the dashboard so every page/store re-fetches with
    // the new X-Account-Id header — far safer than trying to invalidate
    // dozens of independent caches by hand.
    window.location.assign('/dashboard');
  };
  // Desktop collapse (w-20 vs w-64) is separate from the mobile
  // off-canvas open state — on lg+ the sidebar is always in-flow,
  // on smaller viewports it slides over the content.
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [isDesktop, setIsDesktop] = useState(() =>
    typeof window !== 'undefined' ? window.matchMedia('(min-width: 1024px)').matches : true
  );
  const [platformSettings, setPlatformSettings] = useState<any>(null);

  // Track the lg breakpoint so we can collapse-only on desktop —
  // when the mobile drawer is open we always want the full
  // expanded layout (icons + labels), so we override the collapse
  // state to `false` below the breakpoint.
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 1024px)');
    const handler = (e: MediaQueryListEvent | MediaQueryList) =>
      setIsDesktop('matches' in e ? e.matches : (e as MediaQueryList).matches);
    handler(mq);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  // True only on desktop AND when the user has explicitly collapsed.
  // Everywhere below we read this instead of `sidebarCollapsed` so
  // mobile always renders the expanded drawer content.
  const effectiveCollapsed = isDesktop && sidebarCollapsed;

  // Auto-close the mobile drawer when the user navigates so they
  // don't have to manually dismiss it after every link tap.
  useEffect(() => {
    setMobileSidebarOpen(false);
  }, [location.pathname]);

  // Lock body scroll while the mobile drawer is open so the
  // background page doesn't scroll under the overlay.
  useEffect(() => {
    if (mobileSidebarOpen) {
      const original = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = original;
      };
    }
    return undefined;
  }, [mobileSidebarOpen]);

  const themeOptions: { id: Theme; icon: React.ReactNode; label: string }[] = [
    { id: 'light', icon: <Sun className="w-4 h-4" />, label: t('theme.light') },
    { id: 'system', icon: <Monitor className="w-4 h-4" />, label: t('theme.auto') },
    { id: 'dark', icon: <Moon className="w-4 h-4" />, label: t('theme.dark') },
  ];

  const cycleTheme = () => {
    const order: Theme[] = ['light', 'system', 'dark'];
    const next = order[(order.indexOf(theme) + 1) % order.length];
    setTheme(next);
  };

  // Fetch platform settings for logo
  useEffect(() => {
    const fetchPlatformSettings = async () => {
      try {
        const response = await fetch(`${API_URL}/public/platform-settings`);
        if (response.ok) {
          const data = await response.json();
          setPlatformSettings(data);
        }
      } catch (error) {
        console.error('Failed to fetch platform settings:', error);
      }
    };

    fetchPlatformSettings();
  }, []);

  // Sidebar items with optional `requiredFeature`. Items the user
  // doesn't have access to still render with a lock + tier badge —
  // clicking opens the upgrade-gate modal instead of navigating.
  // Hiding outright would surprise users who expect a stable nav, and
  // rewards reverse-engineering ("which features do I get if I pay?").
  type MenuItem = {
    id: string;
    label: string;
    icon: React.ReactNode;
    path: string;
    badge?: string | null;
    highlight?: boolean;
    requiredFeature?: FeatureKey;
    requiredPlan?: 'pro' | 'agency' | 'enterprise';
  };

  const menuItems: MenuItem[] = [
    {
      id: 'dashboard',
      label: t('nav.dashboard'),
      icon: <LayoutDashboard className="w-5 h-5" />,
      path: '/dashboard',
    },
    {
      id: 'short-links',
      label: t('nav.shortLinks'),
      icon: <Link2 className="w-5 h-5" />,
      path: '/short-links',
    },
    {
      id: 'analytics',
      label: t('nav.analytics'),
      icon: <BarChart3 className="w-5 h-5" />,
      path: '/analytics',
    },
    {
      id: 'pixel-manager',
      label: t('nav.pixelManager'),
      icon: <Target className="w-5 h-5" />,
      path: '/pixel-manager',
      requiredFeature: 'pixelManager',
      requiredPlan: 'pro',
    },
    {
      id: 'meta-capi',
      label: t('nav.metaCapi'),
      icon: <Radio className="w-5 h-5" />,
      path: '/meta-capi',
      requiredFeature: 'metaCapi',
      requiredPlan: 'agency',
    },
    {
      // Google Tag Manager auto-provisioning — split out of the Meta
      // CAPI page so operators looking for "GTM" can find it. Gated
      // at Enterprise tier (gtmAutoSync feature) which mirrors the
      // server-side gate in routes/gtm.js.
      id: 'tag-manager',
      label: t('nav.tagManager'),
      icon: <Tag className="w-5 h-5" />,
      path: '/tag-manager',
      requiredFeature: 'gtmAutoSync',
      requiredPlan: 'enterprise',
    },
    {
      // Owns the WHOLE postback experience (inbound S2S + outbound
      // tracker forwarding + dispatch log) for every page in the
      // operator's account. Sits next to Meta CAPI because the two
      // are usually configured together — postback is the
      // affiliate-side complement to CAPI's ad-network side.
      id: 'tracker-postback',
      label: t('nav.trackerPostback'),
      icon: <Send className="w-5 h-5" />,
      path: '/tracker-postback',
      requiredFeature: 'affiliatePostback',
      requiredPlan: 'pro',
    },
    {
      id: 'custom-domain',
      label: t('nav.customDomain'),
      icon: <Globe className="w-5 h-5" />,
      path: '/custom-domain',
      requiredFeature: 'customDomain',
      requiredPlan: 'pro',
    },
    {
      id: 'payment-history',
      label: t('nav.paymentHistory'),
      icon: <Receipt className="w-5 h-5" />,
      path: '/payment-history',
    },
    {
      id: 'team',
      label: t('nav.team', { defaultValue: 'Team' }),
      icon: <Users className="w-5 h-5" />,
      path: '/team',
    },
    {
      id: 'profile',
      label: t('nav.profileSettings'),
      icon: <Settings className="w-5 h-5" />,
      path: '/profile',
    },
    {
      id: 'upgrade',
      label: t('nav.upgradePlan'),
      icon: <Crown className="w-5 h-5" />,
      path: '/upgrade',
      highlight: effectivePlan === 'free',
    },
  ];

  // Decorate gated items: if the current tier doesn't unlock the
  // feature, render a tier badge ("PRO" / "AGENCY") and route the
  // click through the upgrade modal instead of the actual page.
  const decoratedMenuItems = menuItems.map((item) => {
    if (
      item.requiredFeature &&
      !hasFeature(effectivePlan, item.requiredFeature)
    ) {
      return {
        ...item,
        locked: true,
        badge: (item.requiredPlan || 'pro').toUpperCase(),
      };
    }
    return { ...item, locked: false };
  });

  const handleLogout = () => {
    logout();
    toast.success(t('auth.loggedOut'));
    navigate('/login');
  };

  const isActivePath = (path: string) => {
    return location.pathname === path;
  };

  const handleStopImpersonating = () => {
    stopImpersonating();
    navigate('/admin/users');
    toast.success(t('auth.returnedToAdmin'));
  };

  return (
    <div className="flex h-screen bg-gray-50 flex-col">
      {/* Impersonation Banner */}
      {impersonating && (
        <div className="bg-gradient-to-r from-amber-500 to-orange-500 text-white px-4 py-2 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between z-50 flex-shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            <Shield className="w-5 h-5 shrink-0" />
            <span className="font-medium text-sm truncate">
              {t('auth.adminMode')} <strong>{user?.name || user?.email}</strong>
            </span>
          </div>
          <button
            onClick={handleStopImpersonating}
            className="flex items-center justify-center gap-2 px-4 py-1.5 bg-white/20 hover:bg-white/30 rounded-lg transition-colors text-sm font-medium shrink-0"
          >
            <LogOut className="w-4 h-4" />
            {t('auth.backToAdmin')}
          </button>
        </div>
      )}

      {/* Team workspace banner — when the user is acting inside another
          owner's workspace, make it unmistakable whose data they're
          editing and (for viewers) that the workspace is read-only. */}
      {inTeamWorkspace && (
        <div
          className={`px-4 py-2 flex items-center justify-between gap-3 z-40 flex-shrink-0 text-white ${
            isViewer
              ? 'bg-gradient-to-r from-slate-600 to-slate-700'
              : 'bg-gradient-to-r from-indigo-600 to-blue-600'
          }`}
        >
          <div className="flex items-center gap-2 min-w-0 text-sm">
            {isViewer ? <Eye className="w-4 h-4 shrink-0" /> : <Users className="w-4 h-4 shrink-0" />}
            <span className="truncate">
              {t('team.workspaceBanner', {
                defaultValue: "You're in {{name}}'s workspace",
                name: activeAccount?.name || activeAccount?.email || 'team',
              })}
              {isViewer && (
                <strong className="ml-1">
                  · {t('team.viewerReadOnly', { defaultValue: 'Viewer (read-only)' })}
                </strong>
              )}
            </span>
          </div>
          {selfId && (
            <button
              onClick={() => handleSwitchAccount(selfId)}
              className="px-3 py-1 bg-white/20 hover:bg-white/30 rounded-lg transition-colors text-xs font-medium shrink-0"
            >
              {t('team.backToOwn', { defaultValue: 'Back to my account' })}
            </button>
          )}
        </div>
      )}

      {/* Mobile top bar — visible below the lg breakpoint where the
          sidebar is off-canvas. Holds the hamburger that toggles the
          drawer, plus the logo so the page identity stays visible.
          The desktop sidebar already has its own header, so we hide
          this whole row on lg+ to avoid a duplicate brand bar. */}
      <div className="lg:hidden flex items-center justify-between gap-3 h-14 px-3 sm:px-4 bg-white border-b border-gray-200 flex-shrink-0">
        <button
          type="button"
          onClick={() => setMobileSidebarOpen(true)}
          className="p-2 -ml-2 rounded-lg hover:bg-gray-100 text-gray-700"
          aria-label={t('actions.openMenu', { defaultValue: 'Open menu' })}
        >
          <Menu className="w-6 h-6" />
        </button>

        <button
          type="button"
          onClick={() => navigate('/dashboard')}
          className="flex items-center gap-2 min-w-0"
        >
          {platformSettings?.logo_url ? (
            <>
              <img
                src={`${platformSettings.logo_url}?v=${Date.now()}`}
                alt={platformSettings?.logo_text || 'Logo'}
                className="h-7 w-auto object-contain shrink-0"
              />
              <span className="font-bold text-gray-900 text-sm truncate">
                {platformSettings?.logo_text || platformSettings?.site_name || t('brand.defaultLogoText')}
              </span>
            </>
          ) : (
            <>
              <div className="w-7 h-7 bg-gradient-to-br from-blue-600 to-purple-600 rounded-lg flex items-center justify-center shrink-0">
                <LayoutDashboard className="w-4 h-4 text-white" />
              </div>
              <span className="font-bold text-gray-900 text-sm truncate">
                {platformSettings?.logo_text || t('brand.defaultLogoText')}
              </span>
            </>
          )}
        </button>

        {/* Right-side notification bell — gives quick access to the
            unread badge without forcing the user to open the drawer. */}
        <div className="shrink-0">
          <NotificationBell variant="sidebar-collapsed" />
        </div>
      </div>

      <div className="flex flex-1 overflow-hidden relative">
      {/* Mobile drawer backdrop — clicking it dismisses the drawer.
          Only mounted while open so the overlay doesn't intercept
          taps on desktop. */}
      {mobileSidebarOpen && (
        <button
          type="button"
          aria-label={t('actions.closeMenu', { defaultValue: 'Close menu' })}
          onClick={() => setMobileSidebarOpen(false)}
          className="lg:hidden fixed inset-0 z-40 bg-black/40 backdrop-blur-sm"
        />
      )}

      {/* Sidebar
          Desktop (lg+): in-flow, collapsible between w-20 / w-64.
          Mobile: fixed overlay drawer (w-72) that slides in from the
          left when `mobileSidebarOpen` is true. We deliberately use
          a single <aside> with breakpoint-conditional classes so the
          nav state (selected item, scroll position) is preserved
          across viewport resizes. */}
      <aside
        className={`fixed inset-y-0 left-0 z-50 w-72 transform bg-white border-r border-gray-200 flex flex-col transition-all duration-300 ease-in-out
          ${mobileSidebarOpen ? 'translate-x-0 shadow-2xl' : '-translate-x-full'}
          lg:static lg:translate-x-0 lg:shadow-none lg:z-auto lg:w-auto
          ${sidebarCollapsed ? 'lg:w-20' : 'lg:w-64'}
        `}
      >
        {/* Logo/Brand
            On mobile the drawer always renders full-width (sidebarCollapsed
            is a desktop-only concept), so we force the expanded brand
            block via the `lg:hidden` and `lg:flex` toggles below. */}
        <div className="h-16 border-b border-gray-200 flex items-center justify-between px-4 shrink-0">
          {/* Mobile: always show full logo */}
          <div className="flex items-center gap-2 lg:hidden min-w-0">
            {platformSettings?.logo_url ? (
              <>
                <img
                  src={`${platformSettings.logo_url}?v=${Date.now()}`}
                  alt={platformSettings?.logo_text || 'Logo'}
                  className="h-8 w-auto object-contain shrink-0"
                />
                <span className="font-bold text-gray-900 truncate">
                  {platformSettings?.logo_text || platformSettings?.site_name || t('brand.defaultLogoText')}
                </span>
              </>
            ) : (
              <>
                <div className="w-8 h-8 bg-gradient-to-br from-blue-600 to-purple-600 rounded-lg flex items-center justify-center">
                  <LayoutDashboard className="w-5 h-5 text-white" />
                </div>
                <span className="font-bold text-gray-900">
                  {platformSettings?.logo_text || t('brand.defaultLogoText')}
                </span>
              </>
            )}
          </div>

          {/* Desktop: respects the collapse state */}
          {!sidebarCollapsed && (
            <div className="hidden lg:flex items-center gap-2 min-w-0">
              {platformSettings?.logo_url ? (
                <>
                  <img
                    src={`${platformSettings.logo_url}?v=${Date.now()}`}
                    alt={platformSettings?.logo_text || 'Logo'}
                    className="h-8 w-auto object-contain shrink-0"
                  />
                  <span className="font-bold text-gray-900 truncate">
                    {platformSettings?.logo_text || platformSettings?.site_name || t('brand.defaultLogoText')}
                  </span>
                </>
              ) : (
                <>
                  <div className="w-8 h-8 bg-gradient-to-br from-blue-600 to-purple-600 rounded-lg flex items-center justify-center">
                    <LayoutDashboard className="w-5 h-5 text-white" />
                  </div>
                  <span className="font-bold text-gray-900">
                    {platformSettings?.logo_text || t('brand.defaultLogoText')}
                  </span>
                </>
              )}
            </div>
          )}

          {/* Mobile close button (X) */}
          <button
            onClick={() => setMobileSidebarOpen(false)}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors lg:hidden"
            aria-label={t('actions.closeMenu', { defaultValue: 'Close menu' })}
          >
            <X className="w-5 h-5 text-gray-600" />
          </button>

          {/* Desktop collapse/expand toggle */}
          <button
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors hidden lg:flex"
            aria-label={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {sidebarCollapsed ? (
              <Menu className="w-5 h-5 text-gray-600" />
            ) : (
              <X className="w-5 h-5 text-gray-600" />
            )}
          </button>
        </div>

        {/* User Info Card */}
        {!effectiveCollapsed && (
          <div className="p-4 border-b border-gray-200">
            <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-lg p-3">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-500 rounded-full flex items-center justify-center text-white font-semibold">
                  {user?.name.charAt(0).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">{user?.name}</p>
                  <p className="text-xs text-gray-600 truncate">{user?.email}</p>
                </div>
              </div>
              {/* Plan badge — show the ACTIVE WORKSPACE owner's plan so
                  team members inside an Enterprise workspace don't see
                  their personal Free badge. Exhaustive tier handling so
                  agency/enterprise don't fall through to Free. */}
              {(() => {
                const sub = effectivePlan;
                if (sub === 'enterprise') {
                  return (
                    <div className="flex items-center gap-2 px-2 py-1 bg-gradient-to-r from-slate-200 to-gray-200 rounded text-xs font-semibold text-slate-800">
                      <Crown className="w-3 h-3" />
                      {t('subscription.enterpriseMember')}
                    </div>
                  );
                }
                if (sub === 'agency') {
                  return (
                    <div className="flex items-center gap-2 px-2 py-1 bg-purple-100 rounded text-xs font-semibold text-purple-700">
                      <Crown className="w-3 h-3" />
                      {t('subscription.agencyMember')}
                    </div>
                  );
                }
                if (sub === 'pro') {
                  return (
                    <div className="flex items-center gap-2 px-2 py-1 bg-blue-100 rounded text-xs font-semibold text-blue-700">
                      <Crown className="w-3 h-3" />
                      {t('subscription.proMember')}
                    </div>
                  );
                }
                if (sub === 'plus') {
                  return (
                    <div className="flex items-center gap-2 px-2 py-1 bg-green-100 rounded text-xs font-semibold text-green-700">
                      <Zap className="w-3 h-3" />
                      {t('subscription.plusMember')}
                    </div>
                  );
                }
                return (
                  <div className="px-2 py-1 bg-gray-100 rounded text-xs font-semibold text-gray-700">
                    {t('subscription.freePlan')}
                  </div>
                );
              })()}
            </div>
          </div>
        )}

        {/* Workspace switcher — only when the user belongs to more than
            one workspace (their own + at least one team). Lets members
            hop between the accounts they help manage. */}
        {!effectiveCollapsed && accounts.length > 1 && (
          <div className="px-3 py-2 border-b border-gray-200 relative">
            <button
              type="button"
              onClick={() => setAccountMenuOpen((v) => !v)}
              className="w-full flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors text-left"
            >
              <Users className="w-4 h-4 text-gray-500 shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-xs text-gray-400 leading-none mb-0.5">
                  {t('team.workspace', { defaultValue: 'Workspace' })}
                </p>
                <p className="text-sm font-medium text-gray-800 truncate">
                  {activeAccount?.isSelf
                    ? t('team.myAccount', { defaultValue: 'My account' })
                    : activeAccount?.name || activeAccount?.email || '—'}
                </p>
              </div>
              <ChevronsUpDown className="w-4 h-4 text-gray-400 shrink-0" />
            </button>

            {accountMenuOpen && (
              <div className="absolute left-3 right-3 mt-1 z-50 bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden">
                {accounts.map((acc) => {
                  const isActive = acc.id === effectiveAccountId;
                  return (
                    <button
                      key={acc.id}
                      onClick={() => handleSwitchAccount(acc.id)}
                      className={`w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-gray-50 transition-colors ${
                        isActive ? 'bg-blue-50' : ''
                      }`}
                    >
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-800 truncate">
                          {acc.isSelf
                            ? t('team.myAccount', { defaultValue: 'My account' })
                            : acc.name || acc.email}
                        </p>
                        <p className="text-[11px] text-gray-500 capitalize truncate">
                          {acc.isSelf ? t('team.owner', { defaultValue: 'Owner' }) : acc.role}
                        </p>
                      </div>
                      {isActive && <Check className="w-4 h-4 text-blue-600 shrink-0" />}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Notification bell — first-class sidebar row so the unread
            badge stays in peripheral vision instead of hiding in a
            top header. Lives outside <nav> so its dropdown can
            overflow the aside without being clipped by nav's
            overflow-y-auto. */}
        <div className="px-3 py-2 border-b border-gray-200">
          <NotificationBell
            variant={effectiveCollapsed ? 'sidebar-collapsed' : 'sidebar-expanded'}
          />
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto py-4">
          <ul className="space-y-1 px-3">
            {decoratedMenuItems.map((item) => {
              const isActive = isActivePath(item.path);
              const handleClick = () => {
                // Locked items short-circuit to the upgrade modal
                // instead of navigating to a page that would just 403.
                // We still keep them in the nav so users see what's
                // available at higher tiers — hidden nav punishes
                // upgrades by hiding the value prop.
                if (item.locked && item.requiredFeature) {
                  openUpgradeGate({
                    feature: item.requiredFeature,
                    requiredPlan: item.requiredPlan || 'pro',
                    currentPlan: effectivePlan,
                  });
                  return;
                }
                navigate(item.path);
              };
              return (
                <li key={item.id}>
                  <button
                    onClick={handleClick}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all relative ${
                      isActive
                        ? 'bg-blue-50 text-blue-700 font-medium'
                        : item.highlight
                        ? 'bg-gradient-to-r from-yellow-50 to-orange-50 text-orange-700 hover:from-yellow-100 hover:to-orange-100'
                        : item.locked
                        ? 'text-gray-400 hover:bg-gray-100'
                        : 'text-gray-700 hover:bg-gray-100'
                    }`}
                    title={effectiveCollapsed ? item.label : ''}
                  >
                    <span
                      className={
                        isActive
                          ? 'text-blue-600'
                          : item.highlight
                          ? 'text-orange-600'
                          : item.locked
                          ? 'text-gray-400'
                          : 'text-gray-500'
                      }
                    >
                      {item.icon}
                    </span>
                    {!effectiveCollapsed && (
                      <>
                        <span className="flex-1 text-left text-sm">{item.label}</span>
                        {item.badge && (
                          <span
                            className={`px-2 py-0.5 rounded text-xs font-bold ${
                              item.locked
                                ? 'bg-gray-200 text-gray-600'
                                : 'bg-blue-600 text-white'
                            }`}
                          >
                            {item.badge}
                          </span>
                        )}
                        {isActive && <ChevronRight className="w-4 h-4" />}
                      </>
                    )}
                  </button>
                </li>
              );
            })}
          </ul>

          {/* Admin Panel Link (if admin) */}
          {user?.role === 'admin' && !effectiveCollapsed && (
            <div className="px-3 mt-4">
              <button
                onClick={() => navigate('/admin')}
                className="w-full flex items-center gap-3 px-3 py-2.5 bg-gradient-to-r from-yellow-50 to-orange-50 hover:from-yellow-100 hover:to-orange-100 rounded-lg transition-all border border-yellow-200"
              >
                <Shield className="w-5 h-5 text-yellow-600" />
                <div className="flex-1 text-left">
                  <span className="text-sm font-semibold text-yellow-900">{t('nav.adminPanel')}</span>
                  <p className="text-xs text-yellow-700">{t('nav.adminPanelDescription')}</p>
                </div>
              </button>
            </div>
          )}
        </nav>

        {/* Language + Theme Toggle + Logout */}
        <div className="border-t border-gray-200 p-4 space-y-3">
          {!effectiveCollapsed ? (
            <>
              {/* Language switcher */}
              <div className="flex justify-center">
                <LanguageSwitcher size="sm" variant="light" className="w-full justify-center" />
              </div>

              {/* Theme segmented control */}
              <div
                className="flex items-center gap-1 rounded-lg border border-gray-200 bg-gray-50 p-1"
                role="group"
                aria-label={t('theme.label')}
              >
                {themeOptions.map((opt) => {
                  const isActive = theme === opt.id;
                  return (
                    <button
                      key={opt.id}
                      type="button"
                      onClick={() => setTheme(opt.id)}
                      className={`flex flex-1 items-center justify-center gap-1 rounded-md px-2 py-1.5 text-xs font-medium transition-colors ${
                        isActive
                          ? 'bg-white text-gray-900 shadow-sm'
                          : 'text-gray-500 hover:text-gray-700'
                      }`}
                      title={opt.label}
                      aria-pressed={isActive}
                    >
                      {opt.icon}
                      <span className="hidden xl:inline">{opt.label}</span>
                    </button>
                  );
                })}
              </div>

              <button
                onClick={handleLogout}
                className="w-full flex items-center gap-3 px-3 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors text-sm font-medium"
              >
                <LogOut className="w-5 h-5" />
                <span>{t('actions.logout')}</span>
              </button>
              <p className="text-[10px] text-gray-400 text-center mt-3 leading-tight">
                {t('brand.copyright', { year: new Date().getFullYear() })}
              </p>
            </>
          ) : (
            <>
              <button
                type="button"
                onClick={cycleTheme}
                className="w-full p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors flex justify-center"
                title={t('theme.current', { theme, resolved })}
              >
                {resolved === 'dark' ? <Moon className="w-5 h-5" /> : <Sun className="w-5 h-5" />}
              </button>
              <button
                onClick={handleLogout}
                className="w-full p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors flex justify-center"
                title={t('actions.logout')}
              >
                <LogOut className="w-5 h-5" />
              </button>
            </>
          )}
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-y-auto">
        {children}
      </main>
      </div>
    </div>
  );
};

